'use client'

import { useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useInView, useScrollProgress } from '@/hooks/useInView'
import {
  ArrowRight,
  Hammer,
  MapPin,
  Calendar,
  Rocket,
  Globe,
  Target,
  Heart,
  Lightbulb,
  Award,
  Users,
  Wrench,
  BarChart3,
  Clock,
} from 'lucide-react'
import { AnimatedCounter } from '@/components/ui/AnimatedCounter'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaLink } from '@/components/conversion/AttributedCtaLink'
import { RelatedGuides } from '@/components/seo/RelatedGuides'
import { ABOUT_LINKS } from '@/lib/internal-links'
import { AtAGlance } from '@/components/seo/AtAGlance'

/* ── Team Data (order matches main branch / org chart) ─────── */
const leadership = [
  {
    name: 'Brad Strawbridge',
    role: 'Co-Founder, Visionary & Chief Executive Officer',
    avatar: '/assets/images/founders/brad-strawbridge.jpeg',
    bio: 'Brad Strawbridge is Co-Founder, Visionary, and Chief Executive Officer of BuilderLync, and Founder & CEO of Capital City Roofing. With more than a decade of leadership in construction and home services, Brad has built scalable systems that help contractors grow with discipline, structure, and accountability.',
  },
  {
    name: 'Sean Richard',
    role: 'Co-Founder, Creator, CPO & CISO',
    avatar: '/assets/images/founders/sean-scott-richard.jpeg',
    bio: 'Sean Richard is Co-Founder, Creator, Chief Product Officer, and Chief Information Security Officer of BuilderLync. A marketing strategist, systems architect, and cybersecurity professional, Sean designed BuilderLync to replace scattered apps with one connected contractor operating system.',
  },
  {
    name: 'Edward Oueilhe',
    role: 'Integrator & Chief Operating Officer',
    avatar: '/assets/images/founders/edward-oueilhe.png',
    bio: 'Edward Oueilhe is Integrator and Chief Operating Officer of BuilderLync and Capital City Roofing. He serves as the operational engine behind both companies, turning vision into execution through systems, scorecards, accountability, and team alignment.',
  },
  {
    name: 'Blake Grissom',
    role: 'Co-Founder & Chief Sales Officer',
    avatar: '/assets/images/founders/michael-blake-grissom.png',
    bio: 'Blake Grissom is Co-Founder and Chief Sales Officer of BuilderLync. With more than 20 years in the roofing and solar industry, Blake has worked from the field to the top sales ranks, closing more than seven million dollars in a single year.',
  },
  {
    name: 'James Kuntz',
    role: 'Co-Founder & Chief Growth Officer',
    avatar: '/assets/images/founders/james-wolfgang-kuntz.png',
    bio: 'James Kuntz is Co-Founder and Chief Growth Officer of BuilderLync. He built his career in roofing and construction before moving into technology sales, giving him firsthand insight into the daily challenges contractors face.',
  },
  {
    name: 'Nick Xenos',
    role: 'Chief Technology Officer',
    avatar: '/assets/images/founders/nick-xenos.jpg',
    bio: 'Nick Xenos is Chief Technology Officer of BuilderLync, bringing more than a decade of experience building enterprise-scale software across biotech, automotive, marketing technology, and SaaS. He specializes in AI framework development and cloud infrastructure.',
  },
]

const timeline = [
  {
    year: '2024',
    title: 'Founded',
    description:
      'BuilderLync was born in Wilmington, DE — built by roofers who were tired of duct-taping 8 tools together.',
    icon: Hammer,
  },
  {
    year: '2025',
    title: 'Beta Launch',
    description:
      'Early access opened to select roofing contractors. Real-world feedback shaped every feature.',
    icon: Rocket,
  },
  {
    year: '2026',
    title: 'Public Launch',
    description:
      'BuilderLync goes live to contractors nationwide with AI-powered CRM, estimating, and automation.',
    icon: Globe,
  },
]

const values = [
  {
    icon: Target,
    title: 'Built for Contractors',
    description:
      'Every feature is designed specifically for the contracting industry, not adapted from generic business software.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation First',
    description:
      'We leverage cutting-edge AI and automation to solve real problems contractors face every day.',
  },
  {
    icon: Users,
    title: 'Customer-Driven',
    description:
      'Our roadmap is shaped by actual contractor feedback, ensuring we build what you really need.',
  },
  {
    icon: Award,
    title: 'Reliability',
    description:
      'Your business depends on our platform, so we prioritize uptime, security, and consistent performance.',
  },
]

const industryStats = [
  { number: '9/10', label: 'Contractors use 5+ disconnected tools just to manage one project.' },
  { number: '62%', label: 'Lose leads or delay jobs because their systems don\'t communicate.' },
  { number: '40+ hrs/mo', label: 'Wasted switching between apps and double-entering data.' },
  { number: '84%', label: 'Still rely on outdated or mismatched systems that weren\'t built for construction.' },
]

const platformStats = [
  { value: 6, suffix: '', label: 'Co-Founders', icon: Users },
  { value: 8, suffix: '+', label: 'Tools Replaced', icon: Wrench },
  { value: 100, suffix: '+', label: 'DFY Templates', icon: BarChart3 },
  { value: 2000, prefix: '$', suffix: '+', label: 'Saved Monthly Per Customer', icon: Clock },
]

/* ── Timeline Item Component ───────────────────────────────── */
function TimelineItem({
  event,
  index,
  total,
}: {
  event: (typeof timeline)[0]
  index: number
  total: number
}) {
  const { ref, isInView } = useInView()
  const scrollRef = useRef<HTMLDivElement>(null)
  const progress = useScrollProgress(scrollRef)
  const Icon = event.icon
  const isLeft = index % 2 === 0

  return (
    <div ref={scrollRef} className="relative">
      {index < total - 1 && (
        <div
          className="absolute left-8 md:left-1/2 top-6 bottom-0 w-px -translate-x-1/2 origin-top"
          style={{
            background: 'linear-gradient(to bottom, rgba(220,38,38,0.4), rgba(220,38,38,0.05))',
            height: `${Math.min(progress * 100, 100)}%`,
          }}
        />
      )}
      <div
        ref={ref}
        className={`relative flex items-start gap-6 pb-16 md:pb-20 anim-fade-in-up ${isInView ? 'in-view' : ''} ${
          isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
        }`}
      >
        <div
          className={`absolute left-8 md:left-1/2 -translate-x-1/2 z-10 anim-scale-in anim-delay-2 ${isInView ? 'in-view' : ''}`}
        >
          <div className="w-4 h-4 rounded-full bg-red-600 border-4 border-black shadow-[0_0_12px_rgba(220,38,38,0.4)]" />
        </div>
        <div className={`ml-16 md:ml-0 md:w-1/2 ${isLeft ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
          <div className="glass-card-premium p-6 sm:p-8">
            <span className="inline-block text-red-500 font-bold text-sm tracking-wider uppercase mb-2">
              {event.year}
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{event.title}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{event.description}</p>
          </div>
        </div>
        <div className={`hidden md:flex md:w-1/2 ${isLeft ? 'md:pl-16' : 'md:pr-16 justify-end'}`}>
          <div
            className={`w-14 h-14 rounded-2xl bg-red-600/10 border border-red-600/20 flex items-center justify-center anim-scale-in anim-delay-3 ${isInView ? 'in-view' : ''}`}
          >
            <Icon size={26} className="text-red-500" />
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Team Member Card ──────────────────────────────────────── */
function TeamMemberCard({ member, index }: { member: (typeof leadership)[0]; index: number }) {
  return (
    <div
      className="group text-center hover:-translate-y-1.5 transition-transform duration-300"
    >
      {/* Avatar */}
      <div className="relative mb-5 mx-auto w-36 h-36 sm:w-40 sm:h-40">
        <div className="w-full h-full rounded-full overflow-hidden border-[3px] border-zinc-700/60 group-hover:border-red-600/60 transition-colors duration-500 shadow-2xl">
          <Image
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
            src={member.avatar}
            alt={`${member.name}, ${member.role} at BuilderLync`}
            width={160}
            height={160}
            loading="lazy"
          />
        </div>
        {/* Hover red accent line */}
        <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-10 h-[3px] bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Info */}
      <h3 className="text-lg font-bold text-white group-hover:text-red-400 transition-colors duration-300 mb-1">
        {member.name}
      </h3>
      <p className="text-red-400/80 font-semibold text-xs uppercase tracking-wider mb-3">
        {member.role}
      </p>
      <p className="text-zinc-400 text-sm leading-relaxed">
        {member.bio}
      </p>
    </div>
  )
}

/* ── Page Component ────────────────────────────────────────── */
export default function AboutPage() {
  const { ref: statsRef, isInView: statsInView } = useInView()
  const { ref: missionRef, isInView: missionInView } = useInView()
  const { ref: problemRef, isInView: problemInView } = useInView()
  const { ref: problemStatsRef, isInView: problemStatsInView } = useInView()
  const { ref: futureRef, isInView: futureInView } = useInView()
  const { ref: valuesHeaderRef, isInView: valuesHeaderInView } = useInView()
  const { ref: valuesGridRef, isInView: valuesGridInView } = useInView()
  const { ref: leaderHeaderRef, isInView: leaderHeaderInView } = useInView()
  const { ref: leaderGridRef, isInView: leaderGridInView } = useInView()
  const { ref: leaderTaglineRef, isInView: leaderTaglineInView } = useInView()
  const { ref: timelineHeaderRef, isInView: timelineHeaderInView } = useInView()
  const { ref: ctaRef, isInView: ctaInView } = useInView()

  return (
    <div className="min-h-screen">
      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="section relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-hero pointer-events-none" />

        <div className="section-inner text-center relative z-10 py-8 sm:py-12 lg:py-16">
          <div
            className="anim-fade-in-up in-view inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 backdrop-blur-sm mb-8"
          >
            <Hammer size={14} className="text-red-500" />
            <span className="text-sm text-zinc-400">Our story</span>
          </div>

          <h1
            className="anim-fade-in-up anim-delay-1 in-view text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.1] tracking-[-0.03em] mb-6"
          >
            Built for Contractors.{' '}
            <br />
            <span className="gradient-text-red">Powered by AI.</span>
          </h1>

          <p
            className="anim-fade-in-up anim-delay-2 in-view text-lg sm:text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed"
          >
            BuilderLync was created from a simple idea: contractors are some of the most skilled and
            driven professionals in the world, but the software they rely on hasn&apos;t kept up with
            how they actually work.
          </p>
        </div>
      </section>

      {/* ── Our Mission ────────────────────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div
            ref={missionRef}
            className={`anim-fade-in-up ${missionInView ? 'in-view' : ''} glass-card-premium p-10 sm:p-14 text-center relative overflow-hidden`}
          >
            <div
              className="absolute inset-0 pointer-events-none opacity-30"
              style={{
                background: 'radial-gradient(ellipse at 50% 0%, rgba(220,38,38,0.12) 0%, transparent 70%)',
              }}
            />
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
                Our <span className="gradient-text-red">Mission</span>
              </h2>
              <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-4xl mx-auto">
                To create the first AI-powered operating system built exclusively for contractors —
                a platform that understands the way you work, anticipates your needs, and grows with
                your business. We believe contractors deserve technology that works as hard as they do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── The Problem We're Solving ──────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Problem text */}
            <div
              ref={problemRef}
              className={`anim-fade-in-left ${problemInView ? 'in-view' : ''}`}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-8 leading-tight">
                The Problem We&apos;re{' '}
                <span className="gradient-text-red">Solving</span>
              </h2>
              <div className="space-y-5 text-zinc-400 leading-relaxed text-base sm:text-lg">
                <p>
                  Contractors are buried under disconnected tools. CRMs built for sales teams.
                  Project software designed for tech companies. Communication apps that don&apos;t
                  understand the construction workflow.
                </p>
                <p>
                  The result? Missed leads, delayed jobs, frustrated teams, and hours lost every
                  week switching between systems that don&apos;t talk to each other. It&apos;s time
                  contractors had a platform built for the way they actually work.
                </p>
                <p>
                  <strong className="text-white">84% of contractors</strong> still rely on outdated
                  or mismatched systems because nothing on the market truly fits their business.
                </p>
              </div>

              <div className="flex items-center gap-8 mt-10">
                <div className="flex items-center gap-2.5 text-sm text-zinc-400">
                  <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center">
                    <Calendar size={14} className="text-red-500" />
                  </div>
                  Founded 2024
                </div>
                <div className="flex items-center gap-2.5 text-sm text-zinc-400">
                  <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center">
                    <MapPin size={14} className="text-red-500" />
                  </div>
                  Jacksonville, FL
                </div>
              </div>
            </div>

            {/* Industry Stats Card */}
            <div
              ref={problemStatsRef}
              className={`anim-fade-in-right anim-delay-1 ${problemStatsInView ? 'in-view' : ''} glass-card-premium p-8 sm:p-10`}
            >
              <h3 className="text-2xl font-bold mb-8 text-center">
                Industry <span className="gradient-text-red">Impact</span>
              </h3>
              <div className="grid grid-cols-2 gap-6">
                {industryStats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-red-500 mb-2">{stat.number}</div>
                    <div className="text-zinc-400 text-xs sm:text-sm leading-snug">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── The Future We're Building ──────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div
            ref={futureRef}
            className={`anim-fade-in-up ${futureInView ? 'in-view' : ''} glass-card-premium p-10 sm:p-14 relative overflow-hidden`}
          >
            <div
              className="absolute inset-0 pointer-events-none opacity-20"
              style={{
                background: 'radial-gradient(ellipse at 30% 50%, rgba(220,38,38,0.1) 0%, transparent 60%)',
              }}
            />
            <div className="relative z-10 max-w-4xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8">
                The Future We&apos;re <span className="gradient-text-red">Building</span>
              </h2>
              <div className="space-y-6 text-zinc-300 text-base sm:text-lg leading-relaxed">
                <p>
                  Imagine a world where contractors spend their time building — not chasing leads or
                  managing disconnected tools. A world where your CRM, ads, estimates, and follow-ups
                  work together automatically, powered by AI that never sleeps.
                </p>
                <p>
                  That&apos;s the future we&apos;re creating with BuilderLync. A system that connects
                  every part of your business into one smart platform — from your first lead to your
                  final invoice. Sierra, your built-in AI assistant, follows up instantly, schedules
                  appointments, tracks jobs, and keeps your team aligned without you lifting a finger.
                </p>
                <p>
                  For you, that means fewer headaches, more control, and more profit. It means every
                  ad dollar is tracked, every lead is answered, and every project moves faster with
                  less effort.
                </p>
                <p className="font-medium text-white">
                  BuilderLync is built to give contractors their time back — time to grow, to lead,
                  and to live.
                </p>
                <p className="text-zinc-400 text-base italic">
                  That&apos;s the future we&apos;re building: one where technology finally works for
                  the people who build everything else.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ─────────────────────────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div ref={valuesHeaderRef}>
            <h2
              className={`anim-fade-in-up ${valuesHeaderInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4`}
            >
              Our <span className="gradient-text-red">Values</span>
            </h2>
            <p
              className={`anim-fade-in-up anim-delay-1 ${valuesHeaderInView ? 'in-view' : ''} text-zinc-400 text-center mb-12 max-w-xl mx-auto`}
            >
              The principles that guide every feature, decision, and line of code.
            </p>
          </div>

          <div
            ref={valuesGridRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
          >
            {values.map((value, i) => {
              const Icon = value.icon
              return (
                <div
                  key={value.title}
                  className={`anim-fade-in-up anim-delay-${i + 1} ${valuesGridInView ? 'in-view' : ''} glass-card-premium p-6 sm:p-8 group text-center`}
                >
                  <div className="w-12 h-12 rounded-xl bg-red-600/10 border border-red-600/10 flex items-center justify-center mx-auto mb-5 transition-all duration-300 group-hover:bg-red-600/20 group-hover:border-red-600/30 group-hover:shadow-[0_0_20px_rgba(220,38,38,0.15)]">
                    <Icon size={22} className="text-red-500" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{value.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Platform Stats ─────────────────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div
            ref={statsRef}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          >
            {platformStats.map((stat, i) => {
              const Icon = stat.icon
              return (
                <div
                  key={stat.label}
                  className={`anim-scale-in anim-delay-${i + 1} ${statsInView ? 'in-view' : ''} glass-card-premium p-6 sm:p-8 text-center group`}
                >
                  <div className="w-10 h-10 rounded-xl bg-red-600/10 flex items-center justify-center mx-auto mb-4 transition-colors duration-300 group-hover:bg-red-600/20">
                    <Icon size={20} className="text-red-500" />
                  </div>
                  <AnimatedCounter
                    value={stat.value}
                    prefix={stat.prefix ?? ''}
                    suffix={stat.suffix}
                    duration={2000}
                    className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight block mb-1.5"
                  />
                  <span className="text-xs sm:text-sm text-zinc-500 font-medium">{stat.label}</span>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Leadership Team ────────────────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div ref={leaderHeaderRef}>
            <h2
              className={`anim-fade-in-up ${leaderHeaderInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4`}
            >
              The Leadership Behind <span className="gradient-text-red">BuilderLync</span>
            </h2>
            <p
              className={`anim-fade-in-up anim-delay-1 ${leaderHeaderInView ? 'in-view' : ''} text-zinc-400 text-center mb-14 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed`}
            >
              BuilderLync was created by people who know the trades inside and out. Our leadership team
              built their careers in roofing, construction, and sales before turning to technology to
              solve the daily problems contractors face.
            </p>
          </div>

          <div
            ref={leaderGridRef}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 max-w-5xl mx-auto"
          >
            {leadership.map((member, i) => (
              <div
                key={member.name}
                className={`anim-fade-in-up anim-delay-${Math.min(i + 1, 6)} ${leaderGridInView ? 'in-view' : ''}`}
              >
                <TeamMemberCard member={member} index={i} />
              </div>
            ))}
          </div>

          {/* Bottom tagline */}
          <div
            ref={leaderTaglineRef}
            className={`anim-fade-in-up ${leaderTaglineInView ? 'in-view' : ''} mt-16 glass-card-premium p-8 sm:p-10 text-center max-w-3xl mx-auto`}
          >
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
              Built by People Who Understand Your Business
            </h3>
            <p className="text-zinc-400 leading-relaxed">
              We&apos;re not just building software — we&apos;re building the future of how contractors
              work. Our leadership team has lived the challenges you face every day, and we&apos;re
              committed to solving them with technology that actually works.
            </p>
          </div>
        </div>
      </section>

      {/* ── Timeline ──────────────────────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div ref={timelineHeaderRef}>
            <h2
              className={`anim-fade-in-up ${timelineHeaderInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4`}
            >
              Our <span className="gradient-text-red">Journey</span>
            </h2>
            <p
              className={`anim-fade-in-up anim-delay-1 ${timelineHeaderInView ? 'in-view' : ''} text-zinc-400 text-center mb-14 max-w-xl mx-auto`}
            >
              From frustration to innovation — the story of how BuilderLync came to life.
            </p>
          </div>

          <div className="max-w-3xl mx-auto relative">
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-zinc-800/50 -translate-x-1/2" />
            {timeline.map((event, i) => (
              <TimelineItem key={event.year} event={event} index={i} total={timeline.length} />
            ))}
          </div>
        </div>
      </section>

      <AtAGlance />
      <RelatedGuides
        badge="Explore"
        cta="Learn more"
        heading="See What We Built"
        subheading="The product, the pricing and the case for one platform."
        guides={ABOUT_LINKS}
      />

      {/* ── Bottom CTA ────────────────────────────────────── */}
      <section className="section">
        <div className="section-inner">
          <div
            ref={ctaRef}
            className={`anim-fade-in-up ${ctaInView ? 'in-view' : ''} glass-card-premium p-10 sm:p-16 text-center relative overflow-hidden`}
          >
            <div
              className="absolute inset-0 pointer-events-none opacity-40"
              style={{
                background: 'radial-gradient(ellipse at 50% 0%, rgba(220,38,38,0.12) 0%, transparent 70%)',
              }}
            />
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                Ready to Run Your Business on{' '}
                <span className="gradient-text-red">One System?</span>
              </h2>
              <p className="text-zinc-400 mb-10 max-w-2xl mx-auto text-lg">
                Book a live demo and see it with your own workflows. Unlimited users, no contracts.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <AttributedCtaLink
                  href={BOOK_A_DEMO_URL}
                  page="about"
                  cta="Bottom CTA — Book a Demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-base px-8 py-4 glow-red-sm group/btn"
                >
                  Book a Demo
                  <ArrowRight size={18} className="transition-transform duration-200 group-hover/btn:translate-x-0.5" />
                </AttributedCtaLink>
                <Link href="/pricing" className="btn-ghost text-base px-8 py-4">
                  See Pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
